import type { ManiaEngineInput } from '@shared/engine/mania/core';
import type { ManiaLane } from '@shared/maniaNote';
import {
  KeyboardManiaInputSource,
  ReplayManiaInputSource,
  type ManiaInputSource,
} from '../mania/maniaInputSource';
import { laneFromClientX } from './pianoPlayfieldGeometry';

/**
 * Piano-mode input source contract. Re-exports the shared
 * {@link ManiaInputSource} interface so existing call sites keep
 * the `PianoInputSource` name without forcing a project-wide rename.
 */
export type PianoInputSource = ManiaInputSource;

/**
 * Live input source for Piano mode. Thin wrapper over
 * {@link KeyboardManiaInputSource} that wires the Piano-specific
 * pointer geometry (flat lanes via {@link laneFromClientX}) and
 * keybind slice (`prefs.pianoLanes`) into the shared mania input
 * implementation.
 *
 * Concrete subclass (rather than a factory) so existing
 * `new KeyboardPianoInputSource(...)` call sites keep their identity
 * — useful for `instanceof` checks during dev tooling.
 */
export class KeyboardPianoInputSource extends KeyboardManiaInputSource {
  constructor(
    canvas: HTMLCanvasElement,
    getBeatAtNow: () => number,
    isClockRunning: () => boolean = () => true,
  ) {
    super({
      mode: 'piano',
      canvas,
      laneFromClientX,
      getBeatAtNow,
      isClockRunning,
    });
  }
}

/**
 * Replay input source for Piano mode. Identical mechanics to the
 * shared {@link ReplayManiaInputSource}; kept as a named subclass so
 * Piano session code can declare `ReplayPianoInputSource` types
 * without leaking the shared base name through the public API.
 */
export class ReplayPianoInputSource extends ReplayManiaInputSource {
  constructor(inputs: ManiaEngineInput[]) {
    super(inputs);
  }
}

export type { ManiaLane };
